import DescriptionIcon from "@mui/icons-material/Description";
import EmailIcon from "@mui/icons-material/EmailOutlined";
import { IconButton, Popover, Tooltip, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { type FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { backendService } from "../../services/backend.service";
import type { PushReportColumn, PushReportType } from "../../utils/models";
import { type DaysInWeek, DaysInWeekSelector } from "../RecurringShift/util/DaysInWeekSelector";
import SystemButton from "../UIToolkit/SystemButton/SystemButton";

// BE uses Sunday-first 0-indexed (0=Sun..6=Sat); Luxon's `weekday` is ISO Mon-first 1-indexed (1=Mon..7=Sun).
const dayToNumber = (day: DaysInWeek): number => DateTime.fromFormat(day, "cccc").weekday % 7;
const numbersToDays = (numbers: number[]): DaysInWeek[] =>
	numbers
		.map((n) => DateTime.fromObject({ weekday: (n === 0 ? 7 : n) as 1 | 2 | 3 | 4 | 5 | 6 | 7 }).toFormat("cccc") as DaysInWeek)
		.filter((d): d is DaysInWeek => Boolean(d));

const queryKey = (departmentId: string, reportType: PushReportType) => ["push-report-schedule", departmentId, reportType] as const;

type Props = {
	departmentId: string;
	reportType: PushReportType;
	/**
	 * The columns this report should carry, for a report type whose content is whatever the host
	 * table is showing (the multi-facility grid). The table's own column picker is the control — this
	 * component just captures what it currently reads, because the picker's choice lives in
	 * localStorage and the cron that sends the email cannot see it.
	 *
	 * Absent for the report types that fix their own content.
	 */
	columns?: PushReportColumn[];
};

export const PushReportSchedule: FC<Props> = ({ departmentId, reportType, columns }) => {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();
	const queryClient = useQueryClient();

	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [selectedDays, setSelectedDays] = useState<DaysInWeek[]>([]);
	const open = Boolean(anchorEl);

	const { data: existingSchedule } = useQuery({
		queryKey: queryKey(departmentId, reportType),
		queryFn: async () => {
			const response = await backendService.getPushReportSchedule({ departmentId, reportType });
			return response.data;
		},
		enabled: open,
		// Avoid background refetches blowing away in-progress edits while the popover is open.
		refetchOnWindowFocus: false,
	});

	useEffect(() => {
		if (open) {
			setSelectedDays(numbersToDays(existingSchedule?.weekdays ?? []));
		}
	}, [open, existingSchedule]);

	const saveMutation = useMutation({
		mutationFn: async (weekdays: number[]) => {
			const response = await backendService.putPushReportSchedule({ departmentId, reportType, weekdays, columns: columns ?? null });
			return response.data;
		},
		onSuccess: () => {
			track("push_report_schedule_saved", { report_type: reportType });
			showSnackbar(t("push.report.schedule.saved"), "success");
			queryClient.invalidateQueries({ queryKey: queryKey(departmentId, reportType) });
			setAnchorEl(null);
		},
		onError: (e: Error) => {
			trackError("push_report_schedule_save_error", { error_message: e?.message, report_type: reportType });
			showSnackbar(t("push.report.schedule.save.error"), "error");
		},
	});

	const handleSave = () => {
		const weekdays = selectedDays.map(dayToNumber).sort((a, b) => a - b);
		saveMutation.mutate(weekdays);
	};

	return (
		<>
			<Tooltip title={t("push.report.schedule.tooltip")}>
				<IconButton
					sx={{ color: "primary.dark" }}
					onClick={(e) => setAnchorEl(e.currentTarget)}
					size="small"
					data-testid={`push-report-schedule-trigger-${reportType}`}
				>
					<EmailIcon />
				</IconButton>
			</Tooltip>
			<Popover
				open={open}
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
			>
				<Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2, width: 500 }}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<DescriptionIcon sx={{ color: "primary.main" }} />
						<Typography variant="h6" sx={{ fontWeight: 600, color: "primary.main" }}>
							{t("push.report.schedule.title")}
						</Typography>
					</Box>
					<Box sx={{ backgroundColor: "#F1F3F980", padding: 2, display: "flex", flexDirection: "column", gap: 2 }}>
						<Typography>{t("push.report.schedule.repeat.weekly")}</Typography>
						<DaysInWeekSelector selectedDays={selectedDays} onChange={setSelectedDays} gap={1.25} />
						<Typography sx={{ color: "primary.dark", fontSize: "14px", fontWeight: 400 }}>
							{selectedDays.length === 0 ? t("push.report.schedule.empty.helper") : t("push.report.schedule.occurs.weekly", { days: selectedDays.join(", ") })}
						</Typography>
					</Box>
					{columns && (
						<Typography sx={{ color: "primary.dark", fontSize: "14px", fontWeight: 400 }} data-testid="push-report-schedule-columns-note">
							{t("push.report.schedule.columns.note", { count: columns.length })}
						</Typography>
					)}
					<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
						<SystemButton buttonVariant="secondary" onClick={() => setAnchorEl(null)} disabled={saveMutation.isPending}>
							{t("cancel")}
						</SystemButton>
						<SystemButton buttonVariant="primary" onClick={handleSave} disabled={saveMutation.isPending || columns?.length === 0}>
							{t("save")}
						</SystemButton>
					</Box>
				</Box>
			</Popover>
		</>
	);
};
